import React from 'react';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { AppLogo } from '../components/AppLogo.js';
import { AppIcon, type IconName } from '../components/AppIcon.js';
import { NavLink } from '../components/NavLink.js';
import { MAIN_NAV_ITEMS, MOBILE_BOTTOM_NAV_ITEMS, ROUTES } from '../routes/config.js';
import { useAuth } from '../features/auth/authContext.js';
import { PreviewProfileContext } from '../features/auth/previewProfileContext.js';

const navigationIcons: Record<string, IconName> = {
  '/dashboard': 'home',
  '/cards': 'card',
  '/services': 'services',
  '/receive': 'link',
  '/activity': 'activity',
  '/settings': 'settings',
  '/support': 'help',
  '/referrals': 'social',
};

export const AppLayout: React.FC<{ readonly preview?: boolean }> = ({ preview = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const [previewName, setPreviewName] = React.useState('');
  const [signOutError, setSignOutError] = React.useState<string | null>(null);
  const destination = (path: string): string =>
    preview ? (path === '/dashboard' ? '/preview' : '/preview' + path) : path;
  const name = preview
    ? previewName || 'Your preview'
    : (auth.userProfile?.displayName ??
      auth.currentUser?.displayName ??
      auth.currentUser?.email ??
      'Your account');
  const title = preview
    ? 'Explore PAY888'
    : (Object.values(ROUTES).find((r) => r.path === location.pathname)?.title ?? 'PAY888');
  const signOut = async (): Promise<void> => {
    try {
      await auth.signOut();
      navigate('/login', { replace: true });
    } catch {
      setSignOutError('Unable to sign out. Please try again.');
    }
  };
  return (
    <div className="app-frame">
      <a className="skip-link" href="#app-content">
        Skip to content
      </a>
      <aside className="desktop-sidebar">
        <div className="sidebar-brand">
          <AppLogo linkTo={destination('/dashboard')} />
        </div>
        <span className="sidebar-caption">YOUR WORKSPACE</span>
        <nav aria-label="Main Application Navigation">
          {MAIN_NAV_ITEMS.map((item) => (
            <NavLink
              key={item.id}
              to={destination(item.path)}
              label={item.navLabel || item.title}
              icon={<AppIcon name={navigationIcons[item.path] ?? 'home'} />}
              matchDescendants={item.id === ROUTES.RECEIVE.id || item.id === ROUTES.CARDS.id}
            />
          ))}
        </nav>
        <div className="sidebar-bottom">
          <div className="sidebar-help">
            <AppIcon name="help" />
            <strong>Support</strong>
            <p>Get help with your account.</p>
            <Link to={destination('/support')}>Visit help center ↗</Link>
          </div>
          <div className="profile-row">
            <span className="avatar">{preview ? 'P' : name[0]?.toUpperCase()}</span>
            <div>
              <strong>{name}</strong>
              <small>{preview ? 'Sample data only' : 'Personal account'}</small>
            </div>
          </div>
          {preview ? (
            <Link className="quiet-link" to="/">
              ← Back to website
            </Link>
          ) : (
            <button className="quiet-link" onClick={() => void signOut()}>
              Sign out
            </button>
          )}
          {signOutError && <p role="alert">{signOutError}</p>}
        </div>
      </aside>
      <div className="app-main-wrapper">
        <header className="app-topbar">
          <div className="mobile-header-logo">
            <AppLogo size="sm" linkTo={destination('/dashboard')} />
          </div>
          <span className="desktop-header-title">{title}</span>
          <div className="topbar-actions">
            <Link
              className="invite-shortcut"
              to={destination('/referrals')}
              aria-label="Invite friends"
            >
              <AppIcon name="social" />
            </Link>
            <span className="mode-pill">
              <span className="status-dot" />
              {preview ? 'Preview mode' : 'Early access'}
            </span>
            <Link className="avatar" to={destination('/settings')} aria-label="Account Settings">
              {preview ? 'P' : name[0]?.toUpperCase()}
            </Link>
          </div>
        </header>
        <main id="app-content" className="app-content">
          {preview && (
            <div className="preview-notice" role="note">
              An interactive look at PAY888. All balances, cards and activity here are examples.
            </div>
          )}
          {preview ? (
            <PreviewProfileContext.Provider
              value={{ displayName: previewName, setDisplayName: setPreviewName }}
            >
              <Outlet />
            </PreviewProfileContext.Provider>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
      <nav className="mobile-bottom-nav" aria-label="Mobile Application Navigation">
        {MOBILE_BOTTOM_NAV_ITEMS.map((item) => (
          <NavLink
            key={item.id}
            to={destination(item.path)}
            label={item.navLabel || item.title}
            icon={<AppIcon name={navigationIcons[item.path] ?? 'home'} />}
            isMobile
            matchDescendants={item.id === ROUTES.RECEIVE.id || item.id === ROUTES.CARDS.id}
          />
        ))}
      </nav>
    </div>
  );
};
